"use client";

import { useEffect, useLayoutEffect } from "react";
import { usePathname } from "next/navigation";
import {
  merchantAnalyticsEnabled,
  setMerchantAnalyticsContext,
  setMerchantSessionRecordingEnabled,
  trackMerchantPageView,
} from "@/lib/analytics/client";
import type { MerchantAnalyticsContext } from "@jurni/schemas/browser/merchant-analytics";
import { getAnalyticsPage } from "@/lib/analytics/page";

export function PosthogIdentity({
  context,
  children,
}: {
  context: MerchantAnalyticsContext;
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const { userId, email, name, shopId, organizationId, isAdmin } = context;
  const canRecordPage = getAnalyticsPage(pathname) !== null;

  // Establish the server-authenticated scope before descendants send events in
  // passive effects; persisted shop UI state can still belong to a previous shop.
  useLayoutEffect(() => {
    setMerchantAnalyticsContext({
      userId,
      email,
      name,
      shopId,
      organizationId,
      isAdmin,
    });
  }, [userId, email, name, shopId, organizationId, isAdmin]);

  // Keep replay running between eligible pages, stopping before excluded content
  // can be captured by the recorder's mutation observer.
  useLayoutEffect(() => {
    if (!merchantAnalyticsEnabled) return;
    if (!userId || !canRecordPage) {
      setMerchantSessionRecordingEnabled(false);
      return;
    }

    let cancelled = false;
    // Load before enabling: the SDK's remote script callback can otherwise start
    // recording after logout or navigation has already stopped it.
    void import("posthog-js/dist/posthog-recorder")
      .then(() => {
        if (!cancelled) setMerchantSessionRecordingEnabled(true);
      })
      .catch(() => {
        console.warn("Merchant session replay could not load");
      });

    return () => {
      cancelled = true;
      setMerchantSessionRecordingEnabled(false);
    };
  }, [userId, canRecordPage]);

  useEffect(() => {
    trackMerchantPageView();
  }, [pathname, userId, shopId, organizationId]);

  return <>{children}</>;
}
